import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    subsetRemover,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { Receptforskrivning } from "@/tabs/Receptforskrivning/types";
import cloneDeep from "lodash/cloneDeep";

export type ReceptforskrivningMall = {
    id: string;
    namn: string;
    typ: "PRIVAT" | "GEMENSAM" | "ANDRAS" | "GEMENSAMPRIVAT" | "ALLA";
    created_by: string;
    created_by_display: {
        user_display: {
            id: string;
            name: string;
        };
    };
    used_at?: string;
    forskrivning: {
        items: Array<
            Partial<Receptforskrivning> & {
                lakemedelId: string;
                nplId: string;
            }
        >;
    };
};

type State = BaseState & {
    mallar: Map<
        string,
        ReceptforskrivningMall & {
            [key: string]: unknown;
        }
    >;
};

export type BaseActionsProps<T> = {
    state: BaseState & T;
    commit: <K>(
        event: "reset" | string,
        payload?: K,
        options?: {
            root?: boolean;
        }
    ) => void;
};

const filterParamConverter = ({ filterParams }: { filterParams: FilterParams }) => {
    return Object.fromEntries(
        Object.entries(filterParams).map(([key, value]) => {
            if (key === "typ") {
                if (value === "ALLA") {
                    return ["typ", ["GEMENSAM", "PRIVAT"]];
                }

                return [key, value];
            }

            return [key, value];
        })
    );
};

const getDefaultState: () => State = () => {
    return {
        mallar: new Map(),
        ...cloneDeep(baseState),
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        mallar: (state: State) => [...state.mallar.values()],
        mallarSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: "mallar",
            }),
        mallById:
            (state: State) =>
            ({ id }: { id?: ReceptforskrivningMall["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.mallar.get(id);
            },
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
            },
        }),
        addMallar: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                mallar = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                mallar: ReceptforskrivningMall[];
            }
        ) =>
            subsetSetter<State, ReceptforskrivningMall>({
                offset,
                subsetName,
                state,
                resetSubset,
                filterParamConverter,
                collectionName: "mallar",
                items: mallar,
            }),
        updateMallar: (
            state: State,
            {
                mallar,
                offset,
            }: {
                mallar: ReceptforskrivningMall[];
                offset: number;
            }
        ) =>
            subsetUpdateSetter<State, ReceptforskrivningMall>({
                offset,
                state,
                filterParamConverter,
                collectionName: "mallar",
                items: mallar,
            }),
        removeMallar: (
            state: State,
            {
                ids,
            }: {
                ids: Array<ReceptforskrivningMall["id"]>;
            }
        ) =>
            subsetRemover<State>({
                state,
                ids,
                collectionName: "mallar",
            }),
    },
    actions: {
        ...baseActions({ moduleName: "receptforskrivningMall" }),
        getMallById: async (
            { commit, state }: BaseActionsProps<State>,
            {
                id,
                addAsNew,
                addAsUpdate,
                config = {},
            }: {
                id: string;
                // Ska hämtade datan stoppas in i newlyCreated subset?
                addAsNew?: boolean;
                /*
                    Ska hämtningen läggas till som newlyUpdated?
                    Existerande data kommer att ignoreras i store och en ny hämtning från BE kommer att göras
                 */
                addAsUpdate?: boolean;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            const foundMall = state.mallar.get(id);

            if (foundMall) {
                return foundMall;
            }

            try {
                const { data: retrievedMall } = await klinikenApi.get(`nll/mallar/${id}/`, getConfig(config));

                if (retrievedMall) {
                    commit("addMallar", {
                        offset: addAsNew ? Object.keys(state.subsets.newlyCreated.ids).length : 0,
                        mallar: [retrievedMall],
                        subsetName: addAsNew ? "newlyCreated" : id,
                    });

                    if (addAsUpdate) {
                        commit("updateMallar", {
                            subsetName: "newlyUpdated",
                            mallar: [retrievedMall],
                            offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                        });
                    }

                    return retrievedMall;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        fetchMallar: async (
            { commit, state }: BaseActionsProps<State>,
            {
                filterParams,
                forceRefresh,
                limit = 20,
                page = 1,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
                forceRefresh?: boolean;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<
                    State,
                    ReceptforskrivningMall
                >({
                    filterParams,
                    state,
                    limit,
                    page,
                    forceRefresh,
                    collectionName: "mallar",
                    apiUrl: "nll/mallar/",
                });

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addMallar", {
                    offset,
                    subsetName,
                    mallar: results,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                    results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createMall: async (
            { commit, state }: BaseActionsProps<State>,
            {
                mall,
            }: {
                mall: ReceptforskrivningMall;
            }
        ) => {
            try {
                const response = await klinikenApi.post("nll/mallar/", mall, getConfig({}));

                commit("addMallar", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    mallar: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateMall: async (
            { commit, state }: BaseActionsProps<State>,
            {
                mall,
            }: {
                mall: ReceptforskrivningMall;
            }
        ) => {
            try {
                const response = await klinikenApi.put(`nll/mallar/${mall.id}/`, mall, getConfig({}));

                commit("updateMallar", {
                    subsetName: "newlyUpdated",
                    mallar: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        removeMall: async (
            { commit }: BaseActionsProps<State>,
            {
                id,
            }: {
                id: ReceptforskrivningMall["id"];
            }
        ) => {
            try {
                await klinikenApi.delete(`nll/mallar/${id}/`, getConfig({}));

                commit("removeMallar", {
                    ids: [id],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
